import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Code, Zap, Layers, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

const Home = () => {
  const heroRef = useRef(null);
  const [currentAnimation, setCurrentAnimation] = useState(0);
  // 0: web, 1: mobile, 2: design, 3: database, 4: api, 5: git, 6: cloud, 7: analytics, 8: pipeline

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);

  // Cycle through all animations: web -> mobile -> design -> database -> api -> git -> cloud -> analytics -> pipeline -> repeat
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnimation((prev) => (prev + 1) % 9);
    }, 7000); // Change animation every 7 seconds (5s visible + 2s transition)

    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[90vh] flex items-center bg-slate-50 dark:bg-background pt-24 pb-20 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 -right-32 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, -30, 0]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute bottom-1/4 -left-32 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -40, 0],
              y: [0, 40, 0]
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          {/* Dot Matrix */}
          <div className="absolute inset-0 opacity-[0.12] dark:opacity-[0.5] pointer-events-none" style={{
            backgroundImage: `radial-gradient(circle, #1060BE 2px, transparent 2px)`,
            backgroundSize: '50px 50px',
            backgroundPosition: '50px 50px'
          }} />
        </div>

        <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <motion.div
              style={{ opacity, y }}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="md:col-span-7"
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6 px-4 py-1.5 bg-primary/10 dark:bg-primary/20 border border-primary/20 rounded-full text-sm font-medium text-primary"
              >
                Product-first engineering
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-5xl md:text-7xl font-bold text-slate-900 dark:text-text-primary leading-[1.1] mb-6 tracking-tight"
              >
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="block mb-2"
                >
                  Engineering software
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="block text-primary"
                >
                  for long-term growth
                </motion.span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-lg md:text-xl text-slate-600 dark:text-text-secondary mb-10 leading-relaxed max-w-2xl"
              >
                We build scalable, adaptable software systems. Product-first approach with engineering quality that stands the test of time.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4 items-start sm:items-center"
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/contact"
                    className="px-6 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold transition-colors duration-200 inline-flex items-center justify-center gap-2 group shadow-lg shadow-primary/20"
                  >
                    Start a conversation
                    <motion.div
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </motion.div>
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/about"
                    className="px-6 py-3.5 text-slate-700 dark:text-text-secondary border-2 border-slate-300 dark:border-slate-700 rounded-md font-semibold hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 hover:border-primary/50 inline-flex items-center justify-center"
                  >
                    About Us
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden md:block md:col-span-5 relative"
            >
              <div className="relative w-full h-full min-h-[400px] flex items-center justify-center py-8">
                <AnimatePresence mode="wait">
                  {/* Web Browser Animation */}
                  {currentAnimation === 0 && (
                    <motion.svg
                      key="browser"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-full h-full max-w-md opacity-80 dark:opacity-70 absolute"
                      viewBox="0 0 400 300"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      {/* Browser Frame */}
                      <motion.rect
                        x="50"
                        y="40"
                        width="300"
                        height="220"
                        rx="8"
                        fill="none"
                        stroke="#1060BE"
                        strokeWidth="2"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                      />

                      {/* Browser Title Bar */}
                      <motion.rect
                        x="50"
                        y="40"
                        width="300"
                        height="30"
                        rx="8"
                        rx2="0"
                        fill="none"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 1.5 }}
                      />

                      {/* Browser Dots */}
                      <motion.circle
                        cx="70"
                        cy="55"
                        r="4"
                        fill="#1060BE"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 2 }}
                      />
                      <motion.circle
                        cx="85"
                        cy="55"
                        r="4"
                        fill="#1060BE"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 2.1 }}
                      />
                      <motion.circle
                        cx="100"
                        cy="55"
                        r="4"
                        fill="#1060BE"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 2.2 }}
                      />

                      {/* Header Rectangle (Navbar) */}
                      <motion.rect
                        x="60"
                        y="85"
                        width="280"
                        height="25"
                        rx="4"
                        fill="none"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        strokeDasharray="5,5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.6 }}
                        transition={{ duration: 1, delay: 2.5 }}
                      />

                      {/* Content Cards */}
                      <motion.rect
                        x="60"
                        y="125"
                        width="130"
                        height="80"
                        rx="6"
                        fill="none"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.7 }}
                        transition={{ duration: 1.2, delay: 3 }}
                      />
                      <motion.rect
                        x="210"
                        y="125"
                        width="130"
                        height="80"
                        rx="6"
                        fill="none"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.7 }}
                        transition={{ duration: 1.2, delay: 3.3 }}
                      />

                      {/* Inner Content Lines */}
                      <motion.line
                        x1="75"
                        y1="145"
                        x2="175"
                        y2="145"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        strokeDasharray="3,3"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.5 }}
                        transition={{ duration: 0.8, delay: 3.8 }}
                      />
                      <motion.line
                        x1="75"
                        y1="165"
                        x2="155"
                        y2="165"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        strokeDasharray="3,3"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.5 }}
                        transition={{ duration: 0.8, delay: 4 }}
                      />
                      <motion.line
                        x1="75"
                        y1="185"
                        x2="135"
                        y2="185"
                        stroke="#1060BE"
                        strokeWidth="1.5"
                        strokeDasharray="3,3"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.5 }}
                        transition={{ duration: 0.8, delay: 4.2 }}
                      />

                      {/* Code Elements (Floating) */}
                      <motion.g
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: [0, 1, 1, 0], y: [10, 0, -5, -10] }}
                        transition={{ duration: 6, repeat: Infinity, delay: 4.5 }}
                      >
                        <text
                          x="220"
                          y="150"
                          fill="#1060BE"
                          fontSize="10"
                          fontFamily="monospace"
                          opacity="0.6"
                        >
                          {`<div>`}
                        </text>
                        <text
                          x="220"
                          y="170"
                          fill="#1060BE"
                          fontSize="10"
                          fontFamily="monospace"
                          opacity="0.6"
                        >
                          {`  <Component />`}
                        </text>
                        <text
                          x="220"
                          y="190"
                          fill="#1060BE"
                          fontSize="10"
                          fontFamily="monospace"
                          opacity="0.6"
                        >
                          {`</div>`}
                        </text>
                      </motion.g>

                      {/* Drawing Hand/Cursor (optional visual indicator) */}
                      <motion.circle
                        cx="320"
                        cy="180"
                        r="8"
                        fill="#1060BE"
                        opacity="0.3"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{
                          scale: [0, 1, 1, 0],
                          opacity: [0, 0.3, 0.3, 0],
                          x: [0, 20, -20, 0],
                          y: [0, -20, 20, 0]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          delay: 5,
                          ease: "easeInOut"
                        }}
                      />
                    </motion.svg>
                  )}

                  {/* Mobile Interface Animation */}
                  {currentAnimation === 1 && (
                    <motion.div
                      key="mobile"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-40 h-56 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 150 200" className="w-full h-full">
                        {/* Phone Frame */}
                        <motion.rect
                          x="20"
                          y="10"
                          width="110"
                          height="180"
                          rx="12"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.2, delay: 2.2 }}
                        />
                        {/* Notch */}
                        <motion.rect
                          x="55"
                          y="10"
                          width="40"
                          height="8"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.5, delay: 2.8 }}
                        />
                        {/* App Content Blocks */}
                        <motion.rect
                          x="30"
                          y="35"
                          width="90"
                          height="20"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="4,4"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 0.8, delay: 3.2 }}
                        />
                        <motion.rect
                          x="30"
                          y="65"
                          width="90"
                          height="40"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.7 }}
                          transition={{ duration: 1, delay: 3.6 }}
                        />
                        <motion.circle
                          cx="75"
                          cy="95"
                          r="8"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 0.6, delay: 4 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Design Tool Animation - Figma-like */}
                  {currentAnimation === 2 && (
                    <motion.div
                      key="design-tool"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-52 h-64 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 250 300" className="w-full h-full">
                        {/* Canvas/Artboard */}
                        <motion.rect
                          x="20"
                          y="20"
                          width="210"
                          height="180"
                          rx="6"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.5, delay: 0.5 }}
                        />

                        {/* Layers Panel */}
                        <motion.rect
                          x="20"
                          y="210"
                          width="100"
                          height="70"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 1 }}
                        />

                        {/* Layer Items */}
                        {[
                          { y: 230, label: 'Header', delay: 1.5 },
                          { y: 250, label: 'Card', delay: 1.7 },
                          { y: 270, label: 'Button', delay: 1.9 }
                        ].map((layer, i) => (
                          <motion.g key={i}>
                            <motion.rect
                              x="30"
                              y={layer.y}
                              width="80"
                              height="12"
                              rx="2"
                              fill="none"
                              stroke="#1060BE"
                              strokeWidth="1"
                              initial={{ pathLength: 0, opacity: 0 }}
                              animate={{ pathLength: 1, opacity: 0.6 }}
                              transition={{ duration: 0.6, delay: layer.delay }}
                            />
                            <motion.text
                              x="35"
                              y={layer.y + 9}
                              fill="#1060BE"
                              fontSize="8"
                              fontFamily="monospace"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: [0, 0.7, 0.7, 0] }}
                              transition={{ duration: 4, repeat: Infinity, delay: layer.delay + 0.3 }}
                            >
                              {layer.label}
                            </motion.text>
                          </motion.g>
                        ))}

                        {/* Design Components on Canvas */}
                        {/* Component 1 - Card */}
                        <motion.rect
                          x="50"
                          y="60"
                          width="80"
                          height="50"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0, scale: 0.8 }}
                          animate={{
                            pathLength: 1,
                            opacity: [0, 1, 1, 0],
                            scale: [0.8, 1, 1, 0.8]
                          }}
                          transition={{
                            pathLength: { duration: 1, delay: 2 },
                            opacity: { duration: 6, repeat: Infinity, delay: 2 },
                            scale: { duration: 6, repeat: Infinity, delay: 2 }
                          }}
                        />

                        {/* Component 2 - Button */}
                        <motion.rect
                          x="60"
                          y="140"
                          width="60"
                          height="25"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0, scale: 0.8 }}
                          animate={{
                            pathLength: 1,
                            opacity: [0, 1, 1, 0],
                            scale: [0.8, 1, 1, 0.8]
                          }}
                          transition={{
                            pathLength: { duration: 1, delay: 2.5 },
                            opacity: { duration: 6, repeat: Infinity, delay: 2.5 },
                            scale: { duration: 6, repeat: Infinity, delay: 2.5 }
                          }}
                        />

                        {/* Selection indicator */}
                        <motion.rect
                          x="48"
                          y="58"
                          width="84"
                          height="54"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          strokeDasharray="4,4"
                          opacity="0.5"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: [0, 0.5, 0.5, 0],
                            x: [48, 58, 48, 48],
                            y: [58, 138, 58, 58]
                          }}
                          transition={{
                            duration: 6,
                            repeat: Infinity,
                            delay: 3,
                            ease: "easeInOut"
                          }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Database Schema Animation */}
                  {currentAnimation === 3 && (
                    <motion.div
                      key="database"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-52 h-64 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 250 300" className="w-full h-full">
                        {/* Database Table 1 */}
                        <motion.rect
                          x="40"
                          y="40"
                          width="80"
                          height="60"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 0.5 }}
                        />
                        <motion.rect
                          x="42"
                          y="42"
                          width="76"
                          height="15"
                          rx="2"
                          fill="#1060BE"
                          fillOpacity="0.1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 1 }}
                        />
                        {/* Table Rows */}
                        {[58, 68, 78].map((y, i) => (
                          <motion.line
                            key={i}
                            x1="42"
                            y1={y}
                            x2="118"
                            y2={y}
                            stroke="#1060BE"
                            strokeWidth="1"
                            strokeDasharray="2,2"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 0.5 }}
                            transition={{ duration: 0.6, delay: 1.2 + i * 0.2 }}
                          />
                        ))}

                        {/* Database Table 2 */}
                        <motion.rect
                          x="130"
                          y="140"
                          width="80"
                          height="60"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 2 }}
                        />
                        <motion.rect
                          x="132"
                          y="142"
                          width="76"
                          height="15"
                          rx="2"
                          fill="#1060BE"
                          fillOpacity="0.1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 2.5 }}
                        />

                        {/* Connection Line */}
                        <motion.path
                          d="M 90 70 Q 110 105 130 140"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="3,3"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 1, delay: 2.8 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* API/Server Flow Animation */}
                  {currentAnimation === 4 && (
                    <motion.div
                      key="api-flow"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-56 h-48 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 280 240" className="w-full h-full">
                        {/* Client */}
                        <motion.rect
                          x="20"
                          y="100"
                          width="60"
                          height="40"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 0.5 }}
                        />
                        <motion.text
                          x="35"
                          y="125"
                          fill="#1060BE"
                          fontSize="10"
                          fontFamily="monospace"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1 }}
                        >
                          Client
                        </motion.text>

                        {/* Arrow */}
                        <motion.path
                          d="M 90 120 L 160 120"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          initial={{ pathLength: 0 }}
                          animate={{
                            pathLength: 1,
                            opacity: [0, 1, 1, 0]
                          }}
                          transition={{
                            pathLength: { duration: 0.8, delay: 1.5 },
                            opacity: { duration: 3, repeat: Infinity, delay: 1.5 }
                          }}
                        />
                        <motion.polygon
                          points="160,120 155,115 155,125"
                          fill="#1060BE"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: [0, 1, 1, 0] }}
                          transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                        />

                        {/* Server */}
                        <motion.rect
                          x="180"
                          y="100"
                          width="60"
                          height="40"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 2 }}
                        />
                        <motion.text
                          x="195"
                          y="125"
                          fill="#1060BE"
                          fontSize="10"
                          fontFamily="monospace"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 2.5 }}
                        >
                          Server
                        </motion.text>
                      </svg>
                    </motion.div>
                  )}

                  {/* Git/GitHub Workflow Animation */}
                  {currentAnimation === 5 && (
                    <motion.div
                      key="git-workflow"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-52 h-64 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 250 300" className="w-full h-full">
                        {/* Main Branch */}
                        <motion.path
                          d="M 40 200 L 40 100 L 150 100"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.5, delay: 0.5 }}
                        />

                        {/* Commits (circles) */}
                        {[
                          { x: 40, y: 200, label: 'init', delay: 1 },
                          { x: 40, y: 160, label: 'feat', delay: 1.5 },
                          { x: 40, y: 120, label: 'fix', delay: 2 },
                          { x: 95, y: 100, label: 'merge', delay: 2.5 }
                        ].map((commit, i) => (
                          <motion.g key={i}>
                            <motion.circle
                              cx={commit.x}
                              cy={commit.y}
                              r="8"
                              fill="#1060BE"
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ duration: 0.4, delay: commit.delay }}
                            />
                            <motion.text
                              x={commit.x - 15}
                              y={commit.y - 15}
                              fill="#1060BE"
                              fontSize="8"
                              fontFamily="monospace"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: [0, 0.7, 0.7, 0] }}
                              transition={{ duration: 4, repeat: Infinity, delay: commit.delay + 0.5 }}
                            >
                              {commit.label}
                            </motion.text>
                          </motion.g>
                        ))}

                        {/* Feature Branch */}
                        <motion.path
                          d="M 40 160 L 100 160 L 100 60"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="3,3"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 1, delay: 3 }}
                        />
                        <motion.circle
                          cx="100"
                          cy="60"
                          r="6"
                          fill="#1060BE"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.4, delay: 3.5 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Cloud Infrastructure Animation */}
                  {currentAnimation === 6 && (
                    <motion.div
                      key="cloud-infra"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-56 h-56 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 280 280" className="w-full h-full">
                        {/* Cloud Shape */}
                        <motion.path
                          d="M 80 100 Q 60 80 40 100 Q 20 100 20 120 Q 20 140 40 140 L 160 140 Q 180 140 180 120 Q 180 100 160 100 Q 140 80 120 100 Z"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.5, delay: 0.5 }}
                        />

                        {/* Servers inside cloud */}
                        {[
                          { x: 70, y: 120, delay: 1.5 },
                          { x: 100, y: 120, delay: 1.7 },
                          { x: 130, y: 120, delay: 1.9 }
                        ].map((server, i) => (
                          <motion.rect
                            key={i}
                            x={server.x}
                            y={server.y}
                            width="15"
                            height="15"
                            rx="2"
                            fill="none"
                            stroke="#1060BE"
                            strokeWidth="1.5"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{
                              scale: [0, 1, 1, 0],
                              opacity: [0, 1, 1, 0]
                            }}
                            transition={{
                              duration: 6,
                              repeat: Infinity,
                              delay: server.delay
                            }}
                          />
                        ))}

                        {/* Connection Lines */}
                        <motion.line
                          x1="100"
                          y1="140"
                          x2="100"
                          y2="180"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="3,3"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 1, delay: 2.5 }}
                        />

                        {/* Database at bottom */}
                        <motion.rect
                          x="80"
                          y="190"
                          width="40"
                          height="30"
                          rx="3"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 3 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Analytics Dashboard Animation */}
                  {currentAnimation === 7 && (
                    <motion.div
                      key="analytics"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-52 h-56 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 260 280" className="w-full h-full">
                        {/* Dashboard Frame */}
                        <motion.rect
                          x="20"
                          y="20"
                          width="220"
                          height="240"
                          rx="6"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1, delay: 0.5 }}
                        />

                        {/* Chart Bars */}
                        {[
                          { x: 50, height: 40, delay: 1 },
                          { x: 80, height: 60, delay: 1.2 },
                          { x: 110, height: 35, delay: 1.4 },
                          { x: 140, height: 70, delay: 1.6 },
                          { x: 170, height: 50, delay: 1.8 }
                        ].map((bar, i) => (
                          <motion.rect
                            key={i}
                            x={bar.x}
                            y={200 - bar.height}
                            width="20"
                            height={bar.height}
                            rx="2"
                            fill="#1060BE"
                            fillOpacity="0.3"
                            stroke="#1060BE"
                            strokeWidth="1.5"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{
                              height: bar.height,
                              opacity: [0, 0.7, 0.7, 0]
                            }}
                            transition={{
                              height: { duration: 0.6, delay: bar.delay },
                              opacity: { duration: 6, repeat: Infinity, delay: bar.delay }
                            }}
                          />
                        ))}

                        {/* Metric Cards */}
                        <motion.rect
                          x="40"
                          y="60"
                          width="70"
                          height="50"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="4,4"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 0.8, delay: 2.5 }}
                        />
                        <motion.rect
                          x="130"
                          y="60"
                          width="70"
                          height="50"
                          rx="4"
                          fill="none"
                          stroke="#1060BE"
                          strokeWidth="1.5"
                          strokeDasharray="4,4"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.6 }}
                          transition={{ duration: 0.8, delay: 2.8 }}
                        />
                      </svg>
                    </motion.div>
                  )}

                  {/* Deployment Pipeline Animation */}
                  {currentAnimation === 8 && (
                    <motion.div
                      key="pipeline"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ duration: 0.8 }}
                      className="w-56 h-48 opacity-70 dark:opacity-60 absolute"
                    >
                      <svg viewBox="0 0 280 240" className="w-full h-full">
                        {/* Pipeline Stages */}
                        {[
                          { x: 30, label: 'Build', delay: 0.5 },
                          { x: 110, label: 'Test', delay: 1.2 },
                          { x: 190, label: 'Deploy', delay: 2 }
                        ].map((stage, i) => (
                          <motion.g key={i}>
                            <motion.rect
                              x={stage.x}
                              y="100"
                              width="60"
                              height="40"
                              rx="4"
                              fill="none"
                              stroke="#1060BE"
                              strokeWidth="1.5"
                              initial={{ pathLength: 0, opacity: 0 }}
                              animate={{ pathLength: 1, opacity: 1 }}
                              transition={{ duration: 1, delay: stage.delay }}
                            />
                            <motion.text
                              x={stage.x + 15}
                              y="125"
                              fill="#1060BE"
                              fontSize="10"
                              fontFamily="monospace"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: stage.delay + 0.5 }}
                            >
                              {stage.label}
                            </motion.text>

                            {/* Arrow between stages */}
                            {i < 2 && (
                              <motion.path
                                d={`M ${stage.x + 60} 120 L ${stage.x + 90} 120`}
                                fill="none"
                                stroke="#1060BE"
                                strokeWidth="2"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{
                                  pathLength: 1,
                                  opacity: [0, 1, 1, 0]
                                }}
                                transition={{
                                  pathLength: { duration: 0.6, delay: stage.delay + 0.8 },
                                  opacity: { duration: 4, repeat: Infinity, delay: stage.delay + 0.8 }
                                }}
                              />
                            )}
                          </motion.g>
                        ))}

                        {/* Progress Indicator */}
                        <motion.circle
                          cx="60"
                          cy="120"
                          r="6"
                          fill="#1060BE"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{
                            scale: [0, 1, 1, 0],
                            opacity: [0, 1, 1, 0],
                            x: [0, 80, 160, 0],
                            y: [0, 0, 0, 0]
                          }}
                          transition={{
                            duration: 6,
                            repeat: Infinity,
                            delay: 1.5,
                            ease: "easeInOut"
                          }}
                        />
                      </svg>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white dark:bg-background relative overflow-hidden">
        <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 dark:text-text-primary mb-4 tracking-tight">
              Engineering quality that scales
            </h2>
            <p className="text-lg text-slate-600 dark:text-text-secondary max-w-2xl leading-relaxed">
              We architect systems for adaptability and longevity, not quick fixes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {[
              {
                title: "Scalable architecture",
                desc: "Systems designed to grow with your business. Built on proven patterns that adapt to changing requirements.",
                icon: Code,
                delay: 0
              },
              {
                title: "Iterative development",
                desc: "Continuous delivery with regular feedback loops. Progress visible early, adjustments made quickly.",
                icon: Zap,
                delay: 0.1
              },
              {
                title: "Maintainable code",
                desc: "Clean, well-documented code that future teams can understand and extend without difficulty.",
                icon: Layers,
                delay: 0.2
              },
              {
                title: "Transparent process",
                desc: "Clear communication at every stage. No surprises, no technical debt hidden in corners.",
                icon: ShieldCheck,
                delay: 0.3
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{
                    duration: 0.5,
                    delay: feature.delay,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="group space-y-3 p-6 rounded-lg bg-slate-50/50 dark:bg-slate-800/30 border border-slate-200/50 dark:border-slate-700/50 hover:border-primary/30 dark:hover:border-primary/30 transition-all duration-300 cursor-default"
                >
                  <div className="flex items-center gap-3 text-primary">
                    <IconComponent size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-text-primary tracking-tight group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 dark:text-text-secondary leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;